import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, Droplet, Cog, Cpu, Wheat, Ship } from 'lucide-react';

const Industries = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const industries = [
    {
      id: 1,
      icon: Droplet,
      title: 'Oil & Gas',
      description: 'Comprehensive supply of oil and gas products, equipment, and solutions for energy sector operations worldwide.',
      image: 'https://images.unsplash.com/photo-1698757803598-e6318a9a22c4',
      services: [
        'Petroleum Products Supply',
        'Gas Equipment & Parts',
        'Refinery Solutions',
        'Energy Sector Logistics',
      ],
    },
    {
      id: 2,
      icon: Cog,
      title: 'Industrial Machinery',
      description: 'Advanced industrial machinery, precision equipment, and manufacturing solutions for global operations.',
      image: 'https://images.unsplash.com/photo-1701448149957-b96dbd1926ff',
      services: [
        'Heavy Machinery Supply',
        'Manufacturing Equipment',
        'Precision Parts & Components',
        'Installation & Maintenance',
      ],
    },
    {
      id: 3,
      icon: Cpu,
      title: 'Electronics & Diodes',
      description: 'High-quality electronic components, diodes, and data center equipment for technology infrastructure needs.',
      image: 'https://images.unsplash.com/photo-1744368191937-8fe5fde474a8',
      services: [
        'Electronic Components',
        'Diodes & Semiconductors',
        'Data Center Equipment',
        'IT Infrastructure Solutions',
      ],
    },
    {
      id: 4,
      icon: Wheat,
      title: 'Agriculture & Commodities',
      description: 'Premium agricultural products including sesame, moringa, coffee, grains, and specialty commodities.',
      image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449',
      services: [
        'Premium Coffee Beans',
        'Sesame & Moringa',
        'Grains & Cereals',
        'Agricultural Export',
      ],
    },
    {
      id: 5,
      icon: Ship,
      title: 'Global Trade & Logistics',
      description: 'End-to-end international trade facilitation, customs clearance, and comprehensive logistics solutions.',
      image: 'https://images.unsplash.com/photo-1586036308218-5ed6553c98b6',
      services: [
        'Import-Export Services',
        'Customs Clearance',
        'Freight Forwarding',
        'Supply Chain Management',
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Industries | Eaglesway Group - Multi-Industry Expertise</title>
        <meta 
          name="description" 
          content="Eaglesway Group serves multiple industries including Oil & Gas, Industrial Machinery, Electronics, Agriculture, and Global Trade with premium solutions." 
        />
      </Helmet>

      <div className="min-h-screen bg-black text-white pt-20">
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2000"
              alt="Industries"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black"></div>
          </div>

          <div className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Industries <span className="text-[#D4AF37]">We Serve</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Multi-industry expertise delivering comprehensive solutions across key global sectors
            </p>
          </div>
        </section>

        {/* Industries Grid */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-16">
              {industries.map((industry, index) => (
                <Card
                  key={industry.id}
                  className={`group bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 hover:border-[#D4AF37] transition-all duration-500 overflow-hidden ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-0 ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
                    {/* Image Side */}
                    <div className={`relative h-80 lg:h-auto overflow-hidden ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                      <img
                        src={industry.image}
                        alt={industry.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                      
                      {/* Icon Overlay */}
                      <div className="absolute top-8 left-8 w-20 h-20 bg-[#D4AF37]/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-[#D4AF37]/50 group-hover:scale-110 transition-transform duration-300">
                        <industry.icon className="w-10 h-10 text-[#D4AF37]" />
                      </div>
                    </div>

                    {/* Content Side */}
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 group-hover:text-[#D4AF37] transition-colors duration-300">
                        {industry.title}
                      </h2>
                      
                      <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                        {industry.description}
                      </p>

                      {/* Services List */}
                      <div className="mb-8">
                        <h3 className="text-[#D4AF37] font-semibold mb-4 text-sm uppercase tracking-wider">
                          Key Services & Solutions
                        </h3>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {industry.services.map((service, idx) => (
                            <li key={idx} className="flex items-center text-gray-400 text-sm">
                              <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mr-3"></div>
                              {service}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* CTA Button */}
                      <div>
                        <Button
                          onClick={() => navigate('/quote')}
                          className="bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold px-8 py-6 transition-all duration-300 group-hover:scale-105"
                        >
                          Request a Quote
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Need a Custom <span className="text-[#D4AF37]">Solution?</span>
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Our team is ready to discuss your specific industry requirements and provide tailored solutions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/quote')}
                className="bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold px-10 py-7 text-lg transition-all duration-300"
              >
                Get Started
                <ArrowRight className="ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/contact')}
                variant="outline"
                className="border-2 border-[#D4AF37] text-[#D4AF37] bg-transparent hover:bg-[#D4AF37] hover:text-black font-semibold px-10 py-7 text-lg transition-all duration-300"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Industries;
